import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { BackgroundAceExportState } from "./useAceExportBackgroundTask";
import { formatElapsedMinutes } from "./aceStabilityResultHelpers";

export interface BackgroundAceExportBannerProps {
  exportState?: BackgroundAceExportState;
  successMessage?: string | null;
  onDismiss?: () => void;
}

function formatElapsed(startedAt: number): string {
  return formatElapsedMinutes(startedAt);
}

export function BackgroundAceExportBanner({
  exportState,
  successMessage,
  onDismiss,
}: BackgroundAceExportBannerProps) {
  const isSuccess = Boolean(successMessage);
  const isQueued = exportState?.queuedBehindAnother ?? false;
  const elapsed = exportState ? formatElapsed(exportState.startedAt) : null;

  return (
    <div
      className={clsx(
        "mb-3 rounded-lg px-3 py-3 ring-1 text-sm",
        isSuccess
          ? "bg-emerald-500/[0.08] ring-emerald-500/25 text-emerald-100"
          : "bg-indigo-500/[0.08] ring-indigo-500/25 text-slate-200",
      )}
      data-testid="background-ace-export-banner"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-2">
            {!isSuccess ? (
              <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin text-indigo-300" />
            ) : null}
            <p className="font-medium">{isSuccess ? successMessage : exportState?.label}</p>
            {!isSuccess && elapsed ? (
              <span className="text-xs text-slate-500 tabular-nums">({elapsed})</span>
            ) : null}
          </div>
          {!isSuccess && exportState ? (
            <>
              <p className="text-xs text-slate-400 leading-relaxed">
                {isQueued
                  ? "Waiting for another Ace export to finish first. Your job will start automatically when the worker is free."
                  : "Running in the background — you can keep working in Agent Review. The Excel file downloads automatically when ready."}
              </p>
              {!isQueued ? (
                <p className="text-xs text-slate-500 leading-relaxed">
                  LOA cases are slow: expect about 1–2 minutes per Ace run. Ten iterations can take
                  15–20 minutes; one hundred can take 1–2 hours.
                </p>
              ) : null}
              <div className="flex justify-between gap-2 text-xs text-slate-400">
                <span className="truncate">{exportState.message}</span>
                {exportState.progress != null && exportState.progress > 0 ? (
                  <span className="tabular-nums shrink-0">{exportState.progress}%</span>
                ) : null}
              </div>
              {exportState.progress != null && exportState.progress > 0 ? (
                <div className="h-2 overflow-hidden rounded-full bg-slate-800">
                  <div
                    className="h-full bg-indigo-500 transition-all duration-300"
                    style={{ width: `${exportState.progress}%` }}
                  />
                </div>
              ) : null}
            </>
          ) : null}
        </div>
        {onDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            className="shrink-0 text-xs text-slate-400 hover:text-slate-200"
          >
            Dismiss
          </button>
        ) : null}
      </div>
    </div>
  );
}
